import { InlineMd } from "../markdown-view";
import { approvalDecided, remoteActionFailed, remoteActionSucceeded, trackSessionStarted } from "../analytics";
import { signInIfNeeded } from "../gate";
import { useMemo, useState, useSyncExternalStore } from "react";
import { sessionTime } from "@foreman/app-core";
import type { InboxItem } from "@foreman/app-core";
import type { InboxEvent, InboxQuestion } from "@foreman/protocol";
import type { Nav } from "../nav";
import { RiskChip, RuleActions, riskOf } from "../approval-ui";
import { AgentLogo, BackButton, Mascot, Spinner, TabBar, copyText, useApp, useHubSummaries } from "../ui";

const OUTCOME: Record<NonNullable<InboxEvent["outcome"]>, string> = {
  approved: "Approved",
  denied: "Denied",
  answered: "Answered",
  "handled-elsewhere": "Handled at your computer",
  expired: "Expired, the session ended",
  dismissed: "Dismissed",
};

const KIND: Record<InboxEvent["kind"], { label: string; color: string }> = {
  approval: { label: "Approval", color: "var(--warn)" },
  question: { label: "Question", color: "var(--accent)" },
  completed: { label: "Finished", color: "var(--ok)" },
  failed: { label: "Error", color: "var(--danger)" },
};

const icon = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" } as const;
const KIND_ICON: Record<InboxEvent["kind"], React.ReactNode> = {
  approval: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="M12 3 5 6v5.5c0 4.2 2.8 7.6 7 9.5 4.2-1.9 7-5.3 7-9.5V6l-7-3Z" />
    </svg>
  ),
  question: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="M9.2 9a3 3 0 1 1 4.3 2.7c-1 .5-1.5 1.1-1.5 2.3M12 17.5v.01" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  ),
  completed: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="m5 12.5 4.2 4.2L19 7" />
    </svg>
  ),
  failed: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="M12 8v5M12 16.5v.01" />
      <path d="M10.3 4.3 2.8 17.5A2 2 0 0 0 4.5 20.5h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
    </svg>
  ),
};

type Tab = "needs" | "updates" | "all";

const STATUS_LINE: Record<InboxEvent["kind"], string> = {
  approval: "Needs your approval",
  question: "Needs your input",
  completed: "Finished",
  failed: "Error",
};

const ICON = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4 4" />
    </svg>
  ),
  more: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5.5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18.5" cy="12" r="1.6" />
    </svg>
  ),
  phone: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </svg>
  ),
  terminal: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <path d="m7.5 10 3 2.5-3 2.5M13 15h3.5" />
    </svg>
  ),
  dismiss: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m9 9 6 6m0-6-6 6" />
    </svg>
  ),
  chev: (
    <svg className="split-chev" width="16" height="16" viewBox="0 0 24 24" {...icon}>
      <path d="m9.5 6 6 6-6 6" />
    </svg>
  ),
  check: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="m5 12.5 4.2 4.2L19 7" />
    </svg>
  ),
  chat: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <path d="M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H11l-4.5 3.5V16.5h0a2 2 0 0 1-2-2v-8Z" />
    </svg>
  ),
  copy: (
    <svg width="18" height="18" viewBox="0 0 24 24" {...icon}>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5v-2a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2" />
    </svg>
  ),
  folder: (
    <svg width="14" height="14" viewBox="0 0 24 24" {...icon}>
      <path d="M3.5 7a2 2 0 0 1 2-2H10l2 2.5h6.5a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V7Z" />
    </svg>
  ),
};

const needsDecision = (e: InboxEvent) => e.state === "open" && (e.kind === "approval" || e.kind === "question");

export function InboxScreen({ nav }: { nav: Nav }) {
  const { hub } = useApp();
  const items = useSyncExternalStore(hub.subscribe, hub.getInbox);
  const summaries = useHubSummaries(hub);
  const stateOf = (id: string) => summaries.find((s) => s.computer.id === id)?.state ?? "offline";
  // Still getting there (connecting, reconnecting, or connected but the list has not arrived): not "All clear", which would be a claim.
  const pending = summaries.some((s) => s.state === "connecting" || s.state === "reconnecting" || (s.state === "ready" && !hub.get(s.computer.id)?.getSnapshot().inboxLoaded));

  const [tabPick, setTabPick] = useState<Tab | null>(null);
  const [query, setQuery] = useState<string | null>(null); // null: search closed
  const q = (query ?? "").trim().toLowerCase();
  const shown = useMemo(
    () => (q ? items.filter(({ computer, event: e }) => [e.title, e.detail, e.cwd, computer.label, e.agent].some((t) => t?.toLowerCase().includes(q))) : items),
    [items, q],
  );
  const needs = shown.filter((i) => needsDecision(i.event));
  const updates = shown.filter((i) => i.event.state === "open" && !needsDecision(i.event));
  const handled = shown.filter((i) => i.event.state === "resolved");
  const allNeeds = items.filter((i) => needsDecision(i.event)).length;
  const allUpdates = items.filter((i) => i.event.state === "open" && !needsDecision(i.event)).length;
  // Until a tab is picked, lead with what needs you, else show everything.
  const tab: Tab = tabPick ?? (allNeeds > 0 ? "needs" : "all");
  const multi = summaries.length > 1;
  const noneReachable = summaries.length > 0 && summaries.every((s) => s.state !== "ready");

  const clearHandled = () => {
    for (const { computer, event } of handled) hub.get(computer.id)?.dismissInbox(event.id);
  };

  const dismissAll = () => {
    for (const { computer, event } of updates) hub.get(computer.id)?.dismissInbox(event.id);
  };

  const external = shown.some(({ event: e }) => e.external && e.kind === "completed" && e.state === "open");

  return (
    <div className="screen" data-screen="inbox">
      <div className="safe-top">
        <div className={"topbar" + (nav.isRoot ? " root" : "")}>
          {!nav.isRoot && <BackButton onClick={nav.back} />}
          <div className="title-col">
            <div className="t">Activity</div>
            <div className="sub">{allNeeds > 0 ? `${allNeeds} waiting for you` : "Updates from your coding agents"}</div>
          </div>
          <div className="inbox-tools">
            <button className="icon-btn" aria-label="Search" aria-pressed={query !== null} onClick={() => setQuery(query === null ? "" : null)}>
              {ICON.search}
            </button>
          </div>
        </div>
        {query !== null && (
          <div className="inbox-search">
            <input className="input" autoFocus placeholder="Search activity" aria-label="Search activity" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        )}
        {items.length > 0 && (
          <div className="inbox-tabs seg" role="tablist" aria-label="Activity view">
            {([["needs", "Needs you", allNeeds], ["updates", "Updates", allUpdates], ["all", "All", 0]] as const).map(([id, label, n]) => (
              <button key={id} role="tab" aria-selected={tab === id} className={"seg-item" + (tab === id ? " on" : "")} onClick={() => setTabPick(id)}>
                {id === "needs" && n > 0 && <span className="inbox-tab-dot" aria-hidden />}
                {label}
                {n > 0 && <span className="inbox-tab-count">{n}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="gate-host">
      <div className="scroll">
        <div className="content inbox-list">
          {summaries.length === 0 ? (
            <div className="centered">
              <div className="big">No computers yet</div>
              <div className="copy">Pair a computer and its approvals, questions and results show up here.</div>
            </div>
          ) : pending && items.length === 0 ? (
            <div className="refreshing" role="status">
              <Spinner />
              Connecting to your computer…
            </div>
          ) : noneReachable && items.length === 0 ? (
            <div className="centered" data-testid="inbox-unreachable">
              <div className="big">Can't reach your computer</div>
              <div className="copy">Approvals and questions show up here once it is back online. Open Brocoded on it, then check again.</div>
            </div>
          ) : items.length === 0 ? (
            <div className="centered" data-testid="inbox-empty">
              <Mascot size={84} front />
              <div className="big">All clear</div>
              <div className="copy">Approvals, questions and results from your agents show up here.</div>
            </div>
          ) : (
            <>
              {tab !== "updates" && needs.length > 0 && <Section title="Needs you" tone="warn" items={needs} multi={multi} stateOf={stateOf} nav={nav} action={tab === "all" ? { label: "View all", onClick: () => setTabPick("needs") } : undefined} />}
              {tab !== "needs" && updates.length > 0 && (
                <Section title="Recent updates" tone="ok" items={updates} multi={multi} stateOf={stateOf} nav={nav} action={tab === "all" ? { label: "View all", onClick: () => setTabPick("updates") } : updates.length > 1 ? { label: "Dismiss all", onClick: dismissAll } : undefined} />
              )}
              {tab === "needs" && needs.length === 0 && <div className="inbox-quiet">Nothing needs you right now.</div>}
              {tab === "updates" && updates.length === 0 && <div className="inbox-quiet">No updates.</div>}
              {external && tab !== "needs" && <div className="inbox-foot">Continue on phone closes Claude in your terminal and picks up the same chat here.</div>}
              {q && shown.length === 0 && <div className="inbox-quiet">Nothing matches “{query}”.</div>}
              {tab === "all" && handled.length > 0 && (
                <>
                  <div className="group-label inbox-group">
                    <span>Handled</span>
                    <button className="text-btn plain" onClick={clearHandled}>
                      Clear
                    </button>
                  </div>
                  <div className="card">
                    {handled.map(({ computer, event }) => (
                      <div className="handled-row" key={event.id} data-event={event.id}>
                        <span className="dot sm" style={{ background: KIND[event.kind].color }} />
                        <span className="row-text">
                          <span className="row-title">{event.title}</span>
                          <span className="row-sub">
                            <span>{event.rule ? `${event.outcome === "denied" ? "Denied" : "Allowed"} by your rule` : OUTCOME[event.outcome ?? "dismissed"]}</span>
                            {event.rule && <span className="mono-rule">{event.rule}</span>}
                            {multi && <span>· {computer.label}</span>}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </div>
      {nav.isRoot && <TabBar nav={nav} />}
    </div>
  );
}

function Section(props: { title: string; tone: "warn" | "ok"; items: InboxItem[]; multi: boolean; stateOf: (id: string) => string; nav: Nav; action?: { label: string; onClick(): void } }) {
  return (
    <>
      <div className="group-label inbox-group">
        <span className="inbox-dot" style={{ background: props.tone === "warn" ? "var(--warn)" : "var(--ok)" }} aria-hidden />
        <span>{props.title}</span>
        <span className="inbox-count">{props.items.length}</span>
        {props.action && (
          <button className="text-btn plain inbox-group-action" onClick={props.action.onClick}>
            {props.action.label}
          </button>
        )}
      </div>
      {props.items.map((item) => (
        <Card key={item.event.id} item={item} multi={props.multi} online={props.stateOf(item.computer.id) === "ready"} nav={props.nav} />
      ))}
    </>
  );
}

function Card({ item, multi, online, nav }: { item: InboxItem; multi: boolean; online: boolean; nav: Nav }) {
  const { hub } = useApp();
  const { computer, event } = item;
  const conn = hub.get(computer.id);
  const [busy, setBusy] = useState<null | "approve" | "deny" | "answer" | "takeover">(null);
  const [error, setError] = useState<string | null>(null);
  const kind = KIND[event.kind];
  const agentName = event.agent === "claude-code" ? "Claude" : event.agent === "codex" ? "Codex" : event.agent === "antigravity" ? "Antigravity" : event.agent;
  // "Claude finished" only repeats the agent name and the Updates heading: lead with what it said instead.
  const plainDone = event.kind === "completed" && /^\S+ finished$/i.test(event.title.trim());
  const agentKey = event.agent === "claude-code" ? "claude" : event.agent === "codex" ? "codex" : event.agent === "antigravity" ? "antigravity" : "other";
  const folder = event.cwd?.split("/").filter(Boolean).pop();

  const respond = async (action: "approve" | "deny" | "answer", answers?: string[]) => {
    if (!conn || busy) return;
    setBusy(action);
    setError(null);
    const t0 = Date.now();
    try {
      await conn.respondInbox(event.id, action, answers);
      remoteActionSucceeded(action === "answer" ? "answer" : "approval", event.id, Date.now() - t0);
      approvalDecided({ decision: action, createdAt: event.createdAt, ok: true, ms: Date.now() - t0 });
    } catch (e) {
      remoteActionFailed(action === "answer" ? "answer" : "approval", "rejected", Date.now() - t0);
      approvalDecided({ decision: action, createdAt: event.createdAt, ok: false, ms: Date.now() - t0 });
      setError((e as Error).message);
    }
    setBusy(null);
  };

  // Claude is idle in a terminal on the computer: the computer ends it there and resumes the same conversation here.
  const takeOver = async () => {
    if (!conn || busy) return;
    setBusy("takeover");
    setError(null);
    try {
      const s = await conn.createSession({ command: "claude", cwd: event.cwd, cols: 80, rows: 24, takeOver: event.sessionId });
      trackSessionStarted({ agent: "claude", how: "takeover" }, conn.getSnapshot().account?.plan);
      remoteActionSucceeded("takeover", s.id);
      conn.refreshSessions();
      nav.push({ name: "terminal", computerId: computer.id, sessionId: s.id });
    } catch (e) {
      if (!signInIfNeeded(e, computer.id, () => void takeOver())) setError((e as Error).message);
    }
    setBusy(null);
  };

  return (
    <div className="card inbox-card" data-event={event.id} data-kind={event.kind}>
      <div className="inbox-head">
        <span className="inbox-logo">
          <AgentLogo agent={agentKey} size={26} />
        </span>
        <span className="inbox-headtext">
          <span className="inbox-agent">
            {agentName}
            {multi && <span className="inbox-sep">· {computer.label}</span>}
          </span>
          <span className="inbox-status" style={{ color: event.kind === "failed" ? kind.color : undefined }}>{STATUS_LINE[event.kind]}{event.external ? " · Terminal" : ""}</span>
        </span>
        <span className="inbox-meta">{sessionTime(event.createdAt)}</span>
      </div>
      {plainDone && event.detail ? <div className="inbox-lead">{event.detail}</div> : plainDone ? null : <div className="inbox-title">{event.title}</div>}
      {folder && (
        <span className="inbox-folder">
          {ICON.folder}
          {folder}
        </span>
      )}
      {event.kind === "approval" && event.state === "open" && (
        <div className="ask-risk">
          <RiskChip risk={riskOf(event)} />
        </div>
      )}
      {event.kind !== "question" && event.detail && !plainDone && <Detail text={event.detail} mono={event.kind === "approval"} />}

      {event.external && event.state === "open" && event.kind !== "completed" && event.kind !== "failed" && (
        <div className="inbox-actions split">
          <button className="split-cell" onClick={() => nav.push({ name: "earlier", computerId: computer.id, sessionId: event.sessionId })}>
            {ICON.chat}
            <span>Open this chat</span>
            {ICON.chev}
          </button>
        </div>
      )}

      {event.kind === "approval" && (
        <Actions online={online} error={error}>
          <button className="btn primary sm" disabled={!online || !!busy} onClick={() => void respond("approve")}>
            {busy === "approve" && <Spinner />}Approve
          </button>
          <button className="btn danger-text sm" disabled={!online || !!busy} onClick={() => void respond("deny")}>
            {busy === "deny" && <Spinner />}Deny
          </button>
        </Actions>
      )}
      {event.kind === "approval" && event.state === "open" && <RuleActions computerId={computer.id} asked={event} disabled={!online || !!busy} onDecide={(allow) => respond(allow ? "approve" : "deny")} />}

      {event.kind === "question" && (
        <QuestionForm questions={event.questions ?? []} disabled={!online} busy={busy} error={error} online={online} onSend={(a) => void respond("answer", a)} onDecline={() => void respond("deny")} />
      )}

      {(event.kind === "completed" || event.kind === "failed") && (
        <div className="inbox-actions split">
          {!event.external && (
            <button className="split-cell" onClick={() => nav.push({ name: "terminal", computerId: computer.id, sessionId: event.sessionId })}>
              {ICON.terminal}
              <span>Open session</span>
              {ICON.chev}
            </button>
          )}
          {event.external && event.kind === "completed" && event.state === "open" && (
            <button className="split-cell" disabled={!online || !!busy} onClick={() => void takeOver()}>
              {busy === "takeover" ? <Spinner /> : ICON.phone}
              <span>Continue on phone</span>
              {ICON.chev}
            </button>
          )}
          <button className="split-cell dismiss" disabled={!online} onClick={() => conn?.dismissInbox(event.id)}>
            {ICON.dismiss}
            <span>Dismiss</span>
          </button>
        </div>
      )}
      {error && event.kind !== "approval" && event.kind !== "question" && (
        <div className="inbox-err" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function Actions({ children, online, error }: { children: React.ReactNode; online: boolean; error: string | null }) {
  return (
    <>
      <div className="inbox-actions">{children}</div>
      {!online && <div className="inbox-err">Not connected to this computer. You can respond once it reconnects.</div>}
      {error && <div className="inbox-err" role="alert">{error}</div>}
    </>
  );
}

/** Long commands and errors are clamped, never silently cut: "Show all" reveals everything and Copy takes it. */
function Detail({ text, mono }: { text: string; mono: boolean }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const long = text.length > 220 || text.split("\n").length > 5;
  return (
    <div className="inbox-detail-wrap">
      <pre className={"inbox-detail" + (mono ? " mono" : "") + (open || !long ? " open" : "")}>{text}</pre>
      {long && (
        <div className="inbox-detail-tools">
          <button className="text-btn plain" onClick={() => setOpen(!open)}>
            {open ? "Show less" : "Show all"}
          </button>
          <button
            className="text-btn plain"
            onClick={async () => {
              if (await copyText(text)) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
    </div>
  );
}

function QuestionForm(props: {
  questions: InboxQuestion[];
  disabled: boolean;
  busy: null | "approve" | "deny" | "answer" | "takeover";
  online: boolean;
  error: string | null;
  onSend(answers: string[]): void;
  onDecline(): void;
}) {
  // per question: chosen option labels, plus free text
  const [picked, setPicked] = useState<string[][]>(() => props.questions.map(() => []));
  const [other, setOther] = useState<string[]>(() => props.questions.map(() => ""));

  const answers = props.questions.map((q, i) => {
    const parts = [...picked[i]!];
    const free = other[i]!.trim();
    if (free) parts.push(free);
    return q.multiSelect ? parts.join(", ") : (free || parts[0] || "");
  });
  const complete = answers.every((a) => a.length > 0);

  const toggle = (qi: number, label: string, multiSelect: boolean) => {
    setPicked((p) => p.map((sel, i) => (i !== qi ? sel : multiSelect ? (sel.includes(label) ? sel.filter((l) => l !== label) : [...sel, label]) : [label])));
    if (!multiSelect) setOther((o) => o.map((t, i) => (i === qi ? "" : t)));
  };

  return (
    <>
      {props.questions.map((q, qi) => (
        <fieldset className="q-block" key={qi} disabled={props.disabled || !!props.busy}>
          {q.header && <span className="tag">{q.header}</span>}
          <legend className="q-text"><InlineMd text={q.question} /></legend>
          {(q.options ?? []).map((o) => {
            const on = picked[qi]!.includes(o.label);
            return (
              <button
                type="button"
                key={o.label}
                className={"q-opt" + (on ? " on" : "")}
                role={q.multiSelect ? "checkbox" : "radio"}
                aria-checked={on}
                onClick={() => toggle(qi, o.label, !!q.multiSelect)}
              >
                <span className={"q-mark" + (q.multiSelect ? " box" : "")} aria-hidden>{on ? "✓" : ""}</span>
                <span className="row-text">
                  <span className="row-title">{o.label}</span>
                  {o.description && <span className="row-sub">{o.description}</span>}
                </span>
              </button>
            );
          })}
          <input
            className="input bg"
            placeholder={q.options?.length ? "Something else…" : "Your answer"}
            aria-label={`Your own answer to: ${q.question}`}
            value={other[qi]}
            maxLength={1000}
            onChange={(e) => {
              const v = e.target.value;
              setOther((o) => o.map((t, i) => (i === qi ? v : t)));
              if (v && !q.multiSelect) setPicked((p) => p.map((sel, i) => (i === qi ? [] : sel)));
            }}
          />
        </fieldset>
      ))}
      <div className="inbox-actions">
        <button className="btn primary sm" disabled={!props.online || !!props.busy || !complete} onClick={() => props.onSend(answers)}>
          {props.busy === "answer" && <Spinner />}Send answers
        </button>
        <button className="btn danger-text sm" disabled={!props.online || !!props.busy} onClick={props.onDecline}>
          {props.busy === "deny" && <Spinner />}Decline
        </button>
      </div>
      {!props.online && <div className="inbox-err">Not connected to this computer. You can respond once it reconnects.</div>}
      {props.error && <div className="inbox-err" role="alert">{props.error}</div>}
    </>
  );
}

